import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Toast } from '../components/Toast';
import { Info, ExternalLink } from 'lucide-react';
export function SettingsGeneralPage() {
  const navigate = useNavigate();
  // Toggle states for discount settings
  const [disableDiscountConversion, setDisableDiscountConversion] = useState(false);
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const handleToggleChange = (setter: React.Dispatch<React.SetStateAction<boolean>>, value: boolean, settingName: string) => {
    setter(value);
    setToastMessage(`${settingName} ${value ? 'enabled' : 'disabled'}`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-9066">
      {/* NavBar */}
      <div className="mb-4 md:mb-6" data-id="element-9067">
        <NavBar tabs={[]} title="General Settings" subtitle="Store information, sync preferences, and data settings" data-id="element-9068" />
      </div>

      {/* Main Content - Full Width */}
      <div className="space-y-4 md:space-y-6" data-id="element-9069">
        {/* Section 1: Store Information */}
        <Card padding="none" data-id="element-9070">
          <div className="p-4 md:p-6 border-b border-[#E2E8F0] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-id="element-9071">
            <div data-id="element-9072">
              <h2 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A]" data-id="element-9073">
                Store Information
              </h2>
              <p className="text-[13px] md:text-[14px] text-[#64748B] mt-1" data-id="element-9074">
                Synced from your Shopify store
              </p>
            </div>
            <a href="https://admin.shopify.com/settings" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[13px] md:text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-9075">
              Edit in Shopify
              <ExternalLink className="w-4 h-4" data-id="element-9076" />
            </a>
          </div>

          <div className="p-4 md:p-6" data-id="element-9077">
            {/* Info Banner */}
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-9078">
              <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-9079" />
              <p className="text-[13px] md:text-[14px] text-[#0369A1]" data-id="element-9080">
                These settings are automatically synced from Shopify. To make
                changes, update your Shopify store settings.
              </p>
            </div>

            {/* Info Rows */}
            <div className="space-y-3 md:space-y-4" data-id="element-9081">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-9082">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-9083">
                  Store Name
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-9084">
                  Acme Wine Co
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-9085">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-9086">
                  Store Email
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-9087">
                  orders@acmewine.com
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-9088">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-9089">
                  Store Address
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right max-w-md" data-id="element-9090">
                  123 Vineyard Lane, Napa Valley, CA 94558
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-9091">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-9092">
                  Currency
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-9093">
                  AUD - Australian Dollar
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 gap-2" data-id="element-9094">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-9095">
                  Timezone
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-9096">
                  Australia/Melbourne (AEST)
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Section 2: Discount Behavior */}
        <Card padding="none" data-id="element-9097">
          <div className="p-4 md:p-6 border-b border-[#E2E8F0]" data-id="element-9098">
            <h2 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A]" data-id="element-9099">
              Discount Settings
            </h2>
            <p className="text-[13px] md:text-[14px] text-[#64748B] mt-1" data-id="element-9100">
              Configure how discounts work with subscriptions
            </p>
          </div>

          <div className="p-4 md:p-6" data-id="element-9101">
            <div className="flex items-start justify-between py-3 md:py-4 gap-4" data-id="element-9102">
              <div className="flex-1 pr-4 md:pr-6" data-id="element-9103">
                <h3 className="text-[15px] md:text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9104">
                  Disable Automatic Discount Conversion
                </h3>
                <p className="text-[13px] md:text-[14px] text-[#64748B]" data-id="element-9105">
                  Prevent one-time discount codes from automatically converting
                  to subscription discounts
                </p>
              </div>
              <button onClick={() => handleToggleChange(setDisableDiscountConversion, !disableDiscountConversion, 'Automatic Discount Conversion')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${disableDiscountConversion ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={disableDiscountConversion} data-id="element-9106">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${disableDiscountConversion ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9107" />
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-9108">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-9109">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-9110" />
            <div className="flex-1" data-id="element-9111">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-9112">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-9113">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}